/**
 * Web mirror of the search-timing stage/category vocabulary.
 *
 * The backend owns the wire vocabulary in
 * `backend/app/services/search_timing_vocabulary.py`: span categories, every
 * fixed stage's key and canonical detail text, the judge-wave keys, and the
 * key grammar for dynamic per-batch spans. This module is the web half of
 * that contract, pinned to the owner by
 * `backend/tests/test_search_timing_client_parity.py`.
 *
 * It is also the *presentation* owner for knowledge the backend never uses:
 * wave display names, synthetic-lane descriptions, and the generic span
 * fallback. The iOS and Android waterfalls mirror those strings (and the
 * category colors in `frontend/static/assets/styles.css`) and are pinned to
 * this module by the same parity test.
 */

/** Closed set of span categories; `.search-timing-bar.<category>` colors key off these. */
export const TIMING_CATEGORIES = ['queue', 'setup', 'retrieval', 'rerank', 'judging', 'finalization'] as const;

export type TimingCategory = (typeof TIMING_CATEGORIES)[number];

/**
 * What each well-known span key measures, for spans that don't carry their
 * own `detail` text (the high-volume retrieval-arm stages ship without one to
 * keep the persisted envelope small, and rows persisted before a stage
 * emitted detail never have it). Includes the legacy v1 keys that persisted
 * runs still carry.
 */
export const TIMING_DETAILS: Record<string, string> = {
  executor_queue: 'Time between submitting this search to the worker pool and its worker starting.',
  judge_setup: 'Builds the judge prompt context and loads reusable judgments for this description and prompt version.',
  query_embedding: 'Creates or loads the vector representation of the search description. A near-zero value usually means it was already cached.',
  retrieval_gate: 'Time waiting for permission to enter the bounded parallel-retrieval section.',
  semantic: 'Vector-similarity query that finds articles semantically related to the description.',
  prior_results: 'Loads eligible results from the previous run so an incremental refresh preserves older matches.',
  bm25: 'Keyword retrieval that favors exact terms from the search description.',
  base_quality: 'Retrieves high-quality candidates from the active ingest window before relevance judging.',
  substring: 'Exact substring retrieval used when the description contains useful literal terms.',
  candidate_assembly: 'Merges retrieval arms, injects eligible prior results, applies filters, and creates the candidate pool.',
  rerank: 'End-to-end wait for the cross-encoder reranker, including its executor queue, cache lookup, and provider request.',
  'rerank.executor_queue': 'Time from rerank submission until its dedicated worker began.',
  'rerank.cache_lookup': 'Looks up candidate scores already cached for this query and reranker.',
  'rerank.score_application': 'Combines cached and provider scores, sorts candidates, and keeps the configured top set.',
  streaming_judge_wave: 'Complete orchestration window for judging fast-arm candidates before candidate assembly and reranking. Child rows show the actual work inside it.',
  speculative_judge_wave: 'Orchestration window for judge calls attempted while semantic reranking was in flight. See its child rows for completed and cut-off calls.',
  post_rerank_judge_wave: 'Complete orchestration window for judging the highest-ranked remaining articles. Child rows separate prefetch, LLM calls, response application, and progress writes.',
  continued_judge_wave: 'Additional judging performed only when the first post-rerank wave did not reach the search\'s stopping condition.',
  fast_judge_wave: 'Complete orchestration window for fast search\'s single deadline-bounded wave of judge calls. Child rows show each worker-side call; calls still outstanding when the deadline arrived appear as unresolved.',
  final_result_preparation: 'Performs the final forced progress write, computes the ordered result IDs, and decides whether empty-page rescue applied. The terminal status write happens immediately after the measured server-pipeline total.',
  early_judging: 'Legacy v1 orchestration window for speculative judging while reranking ran; not pure completed LLM latency.',
  stream_judging: 'Legacy v1 orchestration window for the first judge wave; includes more than remote LLM latency.',
  post_rerank_judging: 'Legacy v1 orchestration window for post-rerank judging; includes prefetch, response application, and writes.',
  continued_judging: 'Legacy v1 orchestration window for an additional judge wave when the stopping condition was not reached.',
  finalization: 'Legacy v1 final progress write and result preparation before the terminal status write.',
};

/** Fallback for spans the vocabulary doesn't know (e.g. from a newer server). */
export const GENERIC_TIMING_DETAIL = 'Measured server-side work on the shared monotonic search timeline.';

/** The canonical description of one span: its own detail, else its stage's, else generic. */
export function timingSpanDetail(span: { key: string; detail?: string }): string {
  return span.detail ?? TIMING_DETAILS[span.key] ?? GENERIC_TIMING_DETAIL;
}

/** Display prefix per judge-wave wire value (`metadata.wave` / span-key prefix). */
export const WAVE_NAMES: Record<string, string> = {
  streaming: 'Streaming',
  speculative: 'Speculative',
  post_rerank: 'Post-rerank',
  continued: 'Continued',
  fast: 'Fast',
};

/** Prefix for spans whose wave is missing or unknown. */
export const WAVE_FALLBACK_NAME = 'Judge';

// Key grammar for dynamic per-batch spans, mirrored from the backend owner.
// Lane grouping matches on these markers, so they are wire contract.
export const LLM_BATCH_MARKER = '.llm_batch.';
export const CUT_OFF_BATCH_MARKER = '.cut_off_batch.';
export const APPLY_BATCH_MARKER = '.apply_batch.';
export const PROGRESS_FLUSH_PREFIX = 'progress_flush.';

/** What each synthetic (grouped) waterfall lane shows, keyed by lane kind. */
export const LANE_DETAILS = {
  llm_calls: 'Each bar is one completed worker-side LLM request. Overlap means requests were in flight concurrently; executor queue time is excluded.',
  cut_off: 'Each bar runs from local submission until reranking ended. These calls were still unresolved at that cutoff and are not reported as completed LLM latency.',
  apply: 'Each bar validates and merges one completed judge response into the in-memory ranking. Database writes are shown separately.',
  progress_flushes: 'Each bar is an actual database write of buffered judgments and the latest visible result/count snapshot; skipped throttled attempts are omitted.',
} as const;
